import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/actions/action";
import SVG from "src/utils/SVG";

interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  path?: string;
  children?: MenuItem[];
  expanded?: boolean;
}

interface AppResponsiveLayoutProps {
  children: React.ReactNode;
}

// 전체 메뉴 구조 정의
const MENU_ITEMS: MenuItem[] = [
  {
    id: "home",
    label: "홈",
    path: "/home",
    icon: "home",
  },
  {
    id: "notice",
    label: "공지",
    path: "/notice-list",
    icon: "notice",
  },
  {
    id: "as",
    label: "A/S",
    icon: "as",
    expanded: false,
    children: [
      {
        id: "as-management",
        label: "A/S 관리",
        path: "/as-list",
        icon: "list",
      },
      {
        id: "repair-list",
        label: "종합 보수 리스트",
        path: "/repair-list",
        icon: "repair",
      },
      /*{
        id: "monthly-settlement",
        label: "월별 정산 리스트",
        path: "/monthly-settlement",
        icon: "settlement",
      },*/
    ],
  },
  {
    id: "schedule",
    label: "일정",
    path: "/schedule",
    icon: "schedule",
  },
  {
    id: "manage",
    label: "매장/업체",
    path: "/manage-list",
    icon: "store",
  },
];

const AppResponsiveLayout: React.FC<AppResponsiveLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const [isMobile, setIsMobile] = useState(false);
  const [menuItems, setMenuItems] = useState(MENU_ITEMS);
  const [selectedMenu, setSelectedMenu] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // 화면 크기 감지
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // PC에서는 기본적으로 사이드바 열기
      setSidebarOpen(!mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 현재 경로에 따라 선택된 메뉴 설정
  useEffect(() => {
    const currentPath = location.pathname;
    const findSelectedMenu = (items: MenuItem[]): string => {
      for (const item of items) {
        if (item.path && currentPath.includes(item.path.split("?")[0])) {
          return item.id;
        }
        if (item.children) {
          const found = findSelectedMenu(item.children);
          if (found) return found;
        }
      }
      return "";
    };

    // A/S 관련 경로인지 확인
    if (currentPath.includes("/as-") || currentPath.includes("/repair-list") || currentPath.includes("/monthly-settlement") || currentPath.includes("/estimate-") || currentPath.includes("/report-") || currentPath.includes("/visitInfo")) {
      setSelectedMenu("as");
      // A/S 메뉴 확장
      setMenuItems((prev) =>
        prev.map((item) => {
          if (item.id === "as") {
            return { ...item, expanded: true };
          }
          return item;
        })
      );
    } else {
      setSelectedMenu(findSelectedMenu(menuItems));
    }
  }, [location.pathname]);

  // 트리 메뉴 토글 (PC용)
  const toggleTreeMenu = (id: string) => {
    setMenuItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return { ...item, expanded: !item.expanded };
        }
        return item;
      })
    );
  };

  // 메뉴 클릭 처리
  const handleMenuClick = (item: MenuItem) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.children) {
      toggleTreeMenu(item.id);
    }
  };

  // userType이 E가 아닌 경우 매장/업체 메뉴 제거
  const filteredMenuItems = menuItems.filter((item) => {
    if (item.id === "manage" && userInfo.user_type !== "E") {
      return false;
    }
    return true;
  });

  // PC용 사이드바 렌더링
  const renderSidebarMenu = () => (
    <>
      <SidebarOverlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <SidebarContainer $isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo>VMD</Logo>
          <ToggleButton onClick={() => setSidebarOpen(!sidebarOpen)}>
            <SVG name={sidebarOpen ? "close" : "menu"} width={24} height={24} />
          </ToggleButton>
        </SidebarHeader>

        <MenuContainer>
          {filteredMenuItems.map((item) => (
            <React.Fragment key={item.id}>
              <MenuItem
                onClick={() => handleMenuClick(item)}
                $hasChildren={!!item.children}
                $isSelected={selectedMenu === item.id || (item.id === "as" && location.pathname.includes("/as-"))}
              >
                <MenuItemContent>
                  <span>{item.label}</span>
                </MenuItemContent>
                {item.children && (
                  <ExpandIcon $expanded={item.expanded}>
                    <SVG name="arrow_down" width={16} height={16} />
                  </ExpandIcon>
                )}
              </MenuItem>

              {item.children && item.expanded && (
                <SubMenuContainer>
                  {item.children.map((subItem) => (
                    <SubMenuItem
                      key={subItem.id}
                      onClick={() => handleMenuClick(subItem)}
                      $isSelected={
                        (subItem.path && location.pathname.includes(subItem.path.split("?")[0])) ||
                        (subItem.id === "as-management" && location.pathname.includes("/as-")) ||
                        (subItem.id === "repair-list" && location.pathname.includes("/repair-list")) ||
                        (subItem.id === "monthly-settlement" && location.pathname.includes("/monthly-settlement"))
                      }
                    >
                      <SubMenuItemContent>
                        <span>{subItem.label}</span>
                      </SubMenuItemContent>
                    </SubMenuItem>
                  ))}
                </SubMenuContainer>
              )}
            </React.Fragment>
          ))}
        </MenuContainer>
      </SidebarContainer>
    </>
  );

  return (
    <Container>
      {!isMobile && renderSidebarMenu()}
      <MainContent $isMobile={isMobile} $sidebarOpen={!isMobile && sidebarOpen}>
        {children}
      </MainContent>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 769px) {
    display: flex;
  }
`;

const SidebarOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 99;

  @media (min-width: 769px) {
    display: none;
  }
`;

const SidebarContainer = styled.div<{ $isOpen: boolean }>`
  width: 260px;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  position: fixed;
  left: ${({ $isOpen }) => ($isOpen ? "0" : "-260px")};
  top: 0;
  bottom: 0;
  z-index: 100;
  transition: left 0.3s ease;
  overflow-y: auto;

  @media (max-width: 768px) {
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px;
  border-bottom: 1px solid #e5e7eb;
  background: #00263e;
`;

const Logo = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 769px) {
    display: none;
  }
`;

const MenuContainer = styled.div`
  padding: 16px 0;
`;

const MenuItem = styled.div<{ $hasChildren: boolean; $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  cursor: pointer;
  background: ${({ $isSelected }) => ($isSelected ? "#f3f4f6" : "transparent")};
  transition: background 0.2s;
  position: relative;

  &:hover {
    background: #f9fafb;
  }

  ${({ $isSelected }) => $isSelected && `
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #00263e;
    }
  `}
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    font-size: 15px;
    font-weight: 500;
    color: #374151;
  }
`;

const ExpandIcon = styled.div<{ $expanded?: boolean }>`
  transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.2s;
  color: #9ca3af;
`;

const SubMenuContainer = styled.div`
  background: #f9fafb;
`;

const SubMenuItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 20px 12px 52px;
  cursor: pointer;
  background: ${({ $isSelected }) => ($isSelected ? "#e5e7eb" : "transparent")};
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const SubMenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-size: 14px;
    color: #4b5563;
  }
`;

const MainContent = styled.div<{ $isMobile: boolean; $sidebarOpen: boolean }>`
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;

  @media (min-width: 769px) {
    flex: 1;
    min-width: 0;
    margin-left: ${({ $sidebarOpen }) => ($sidebarOpen ? "260px" : "0")};
    transition: margin-left 0.3s ease;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

export default AppResponsiveLayout;